/**
 * 62. Valuation Report — the stock on hand now and its value, by product
 * and warehouse.
 *
 * REP-FIELDS (D140): the screen reads exactly what GET /v1/inventory-balances
 * sends: `productSku`, `productName`, `valuationMethod`, `warehouseName`,
 * `quantity`, `unitCost`, `valuation` and its `currencyCode`, and the
 * server's per-currency `meta.valuationTotals` over every page. It used to
 * read six fields the route never sent, so only the quantity showed; its
 * method filter compared a field that was never there and matched nothing,
 * and it is gone (the method is a column); its title counted one page of
 * rows as the whole list. Pages follow the API's cursor. These are the
 * balances now: a valuation at a past date is not available yet (CO-W3), and
 * the page says so. A failed load shows the API's error and Try again.
 *
 * Arabic and English (C7): every word from the catalogs; a valuation method
 * is the glossary's word (الوارد أولاً يُصرف أولاً، المتوسط المرجّح), never the
 * stored value.
 */
import { Card, PageHeader, DataTable, Skeleton } from "@/components/ui";
import { CursorPager } from "@/components/CursorPager";
import { PendingButton, usePendingReason } from "@/components/PendingAction";
import { QueryError } from "@/components/QueryError";
import { useCursorList } from "@/lib/api/use-cursor-list";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatQuantity, formatMoney } from "@/lib/money";

/** One row of GET /v1/inventory-balances, as the route sends it (camelCased). */
interface ValuationRow {
  id: string;
  productSku: string;
  productName: string;
  warehouseName: string;
  valuationMethod: string | null;
  quantity: string;
  /** valuation / quantity at six decimals; null when the quantity is zero. */
  unitCost: string | null;
  valuation: string;
  /** The functional currency of the warehouse's books; null when unknown. */
  currencyCode: string | null;
}

/** GET /v1/inventory-balances `meta`, as sent. */
interface ValuationMeta {
  valuationTotals?: Array<{ currencyCode: string | null; valuation: string }>;
}

/** The slice that brings valuation at a past date (BUILD-PLAN CO-W3). */
const AS_OF_SLICE = "CO-W3";

export function ValuationReportPage() {
  const { t } = useTranslation();
  const asOfWhy = usePendingReason("valuation", AS_OF_SLICE);
  const balances = useCursorList<ValuationRow>(
    ["inventory", "valuation"],
    "/inventory/valuation",
  );
  const { data, isPending, error, refetch, isFetching } = balances;
  const totals = ((data?.meta ?? {}) as ValuationMeta).valuationTotals ?? [];

  return (
    <div>
      <PageHeader
        title={t("inventory.valuation.title")}
        subtitle={t("inventory.valuation.subtitle")}
        actions={
          <PendingButton variant="primary" reason="valuation" slice="CO-W3">
            {t("shared.export")}
          </PendingButton>
        }
      />
      {isPending ? (
        <Skeleton rows={10} />
      ) : error || !data ? (
        <QueryError
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : (
        <Card
          title={
            balances.page === 1 && !balances.hasMore
              ? listTitle(t("inventory.valuation.list"), data.items.length)
              : t("inventory.valuation.list")
          }
        >
          <div role="status" style={{ marginBlockEnd: 12 }}>
            <p style={{ marginBlock: "0 4px" }}>
              {t("inventory.valuation.nowOnly")}
            </p>
            <p style={{ margin: 0 }}>{asOfWhy}</p>
          </div>
          <DataTable<ValuationRow>
            rows={data.items}
            columns={[
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "productName", label: t("fields.product") },
              { key: "warehouseName", label: t("fields.warehouse") },
              {
                key: "valuationMethod",
                label: t("inventory.valuation.method"),
                render: (r) =>
                  r.valuationMethod
                    ? valueLabel(t, "valuationMethod", r.valuationMethod)
                    : "—",
              },
              {
                key: "quantity",
                label: t("fields.qty"),
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              {
                key: "unitCost",
                label: t("inventory.valuation.unitCost"),
                numeric: true,
                render: (r) => formatMoney(r.unitCost, r.currencyCode ?? ""),
              },
              {
                key: "valuation",
                label: t("inventory.valuation.totalValue"),
                numeric: true,
                render: (r) => (
                  <strong>
                    {formatMoney(r.valuation, r.currencyCode ?? "")}
                  </strong>
                ),
              },
            ]}
          />
          <CursorPager
            page={balances.page}
            hasMore={balances.hasMore}
            next={balances.next}
            previous={balances.previous}
            busy={isFetching}
          />
          {totals.length > 0 && (
            <div className="atlas-grid atlas-grid--2 mt-4">
              {totals.map((total) => (
                <Card key={total.currencyCode ?? ""}>
                  <div className="atlas-stat__label">
                    {t("inventory.valuation.totalAllPages")}
                  </div>
                  <div className="atlas-stat__value">
                    {formatMoney(total.valuation, total.currencyCode ?? "")}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
